import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import SearchBar from '@/features/documents/components/search-bar';
import { CreateIntegrationButton } from '@/features/integrations/components/buttons/create-integration-button';
import { TrackingReportPopulated } from '@/features/projects/kpi-tracking-entry/api/get-tracking-reports';
import { ExportTrackingReportsButton } from '@/features/projects/kpi-tracking-entry/components/export-tracking-reports-button';
import { RequestTrackingButton } from '@/features/projects/kpi-tracking-entry/components/request-tracking-button';
import { TrackingReportDialog } from '@/features/projects/kpi-tracking-entry/components/tracking-report-dialog';
import { TrackingReportsTable } from '@/features/projects/kpi-tracking-entry/components/tracking-reports-table';
import { useDeleteTrackingReport } from '@/features/projects/kpi-tracking-entry/hooks/use-delete-tracking-report';
import {
  useInfiniteTrackingReports,
  useTrackingReports,
} from '@/features/projects/kpi-tracking-entry/hooks/use-tracking-reports';
import { useProjectKpisWithMetrics } from '@/features/projects/project-details/hooks/use-project-kpis';
import { ProjectDetails } from '@/features/projects/shared/api/get-project-details';
import { useProjectDetails } from '@/features/projects/shared/hooks/use-project-details';
import { AddButton } from '@/shared/components/actions/add-button';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { EmptyTablePlaceholder } from '@/shared/components/table/empty-table-placeholder';
import { Button } from '@/shared/components/ui/button';
import { KpiWithMetrics } from '@/shared/types/api';

function getTrackingReportColumns(
  t: (...args: any[]) => string,
  isContractor: boolean,
): string[] {
  return [
    isContractor
      ? t('TrackingReportsTable.project_name')
      : t('TrackingReportsTable.sub_project_name'),
    t('TrackingReportsTable.creator'),
    t('TrackingReportsTable.description'),
    t('TrackingReportsTable.kpis'),
    t('TrackingReportsTable.values'),
    t('TrackingReportsTable.documents'),
    t('TrackingReportsTable.date'),
  ];
}

function filterTrackingReports(
  reports: TrackingReportPopulated[],
  searchQuery: string,
): TrackingReportPopulated[] {
  const trimmed = searchQuery.trim();
  if (trimmed.length === 0) return reports;

  const searchLower = trimmed.toLowerCase();
  return reports.filter((report) =>
    report.description?.toLowerCase().includes(searchLower),
  );
}

export const TrackingReportHistoryHub = () => {
  const { t } = useTranslation('project', { keyPrefix: 'TrackingReports' });
  const { projectId } = useParams();
  const navigate = useNavigate();

  if (!projectId) {
    navigate(paths.projects.hub.path);
  }

  const { data: project, isLoading: isProjectLoading } = useProjectDetails({
    projectId: projectId!,
  });

  const [searchQuery, setSearchQuery] = useState<string>('');

  const [trackingReportFormOpen, setTrackingReportFormOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState<
    TrackingReportPopulated | undefined
  >(undefined);

  const isContractor = project?.type === 'subproject_contractor';
  const isClient = project?.type === 'subproject_client';

  const queries = useProjectKpisWithMetrics({
    projectId: project?.id,
    projectType: project?.type,
  });
  // Only use standard KPIs (queries[0]) for tracking entries - derivative KPIs cannot have manual entries
  const standardKpis = (queries[0].data?.filter(Boolean) ||
    []) as KpiWithMetrics[];

  return (
    <DefaultLayout
      title={t('title')}
      headerActions={[
        <SearchBar
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
          }}
          placeholder={t('search_placeholder')}
        />,
        isContractor && projectId && (
          <CreateIntegrationButton projectId={Number(projectId)} />
        ),
        isContractor && (
          <AddButton
            label={t('add_report')}
            onClick={() => {
              setTrackingReportFormOpen(true);
            }}
          />
        ),
        project && isClient && (
          <RequestTrackingButton
            projectId={project.id}
            orgId={project.org_id}
          />
        ),
        <ExportTrackingReportsButton />,
      ]}
    >
      {isProjectLoading ? (
        <LoadingIndicator />
      ) : (
        project && (
          <InfiniteTrackingReportsView
            project={project}
            setTrackingReportFormOpen={setTrackingReportFormOpen}
            setCurrentReport={setCurrentReport}
            searchQuery={searchQuery}
          />
        )
      )}
      {project && (
        <TrackingReportDialog
          project={project}
          kpis={standardKpis.map((kpi) => {
            return {
              ...kpi,
              description: '',
              owner_org_id: null,
            };
          })}
          isOpen={trackingReportFormOpen}
          onClose={() => {
            setTrackingReportFormOpen(false);
            setCurrentReport(undefined);
          }}
          trackingReport={currentReport}
          editMode={!!currentReport}
          isContractor={isContractor}
        />
      )}
    </DefaultLayout>
  );
};

/** Used on the project detail page with a fixed limit (no pagination). */
export const TrackingReportsView = ({
  project,
  setTrackingReportFormOpen,
  setCurrentReport,
  searchQuery,
  limit,
}: {
  project: ProjectDetails;
  setTrackingReportFormOpen: (isOpen: boolean) => void;
  setCurrentReport: (report: TrackingReportPopulated | undefined) => void;
  searchQuery: string;
  limit?: number;
}) => {
  const { data, isLoading, isError } = useTrackingReports({ limit });

  const { t } = useTranslation('project', { keyPrefix: 'TrackingReports' });

  const isContractor = project.type === 'subproject_contractor';

  const columns = getTrackingReportColumns(t, isContractor);

  const hasSearchQuery = searchQuery.trim().length > 0;

  const filteredData = filterTrackingReports(data ?? [], searchQuery);

  const { mutate: deleteTrackingReport } = useDeleteTrackingReport();

  return (
    <div>
      {isLoading ? (
        <LoadingIndicator />
      ) : !data || isError ? (
        <span>{t('Errors.fetch')}</span>
      ) : filteredData.length === 0 ? (
        <EmptyTablePlaceholder
          title={hasSearchQuery ? t('no_reports_found') : t('no_reports')}
          description={
            hasSearchQuery
              ? undefined
              : isContractor
                ? t('no_reports_description')
                : undefined
          }
          columns={columns}
          action={
            hasSearchQuery
              ? undefined
              : isContractor
                ? () => setTrackingReportFormOpen(true)
                : undefined
          }
        />
      ) : (
        <TrackingReportsTable
          isContractor={isContractor}
          reports={filteredData}
          onOpenTrackingReport={(report) => {
            setCurrentReport(report);
            setTrackingReportFormOpen(true);
          }}
          onDeleteTrackingReport={deleteTrackingReport}
          columns={columns}
        />
      )}
    </div>
  );
};

/** Used on the tracking history hub with infinite scroll / load-more. */
const InfiniteTrackingReportsView = ({
  project,
  setTrackingReportFormOpen,
  setCurrentReport,
  searchQuery,
}: {
  project: ProjectDetails;
  setTrackingReportFormOpen: (isOpen: boolean) => void;
  setCurrentReport: (report: TrackingReportPopulated | undefined) => void;
  searchQuery: string;
}) => {
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteTrackingReports();

  const { t } = useTranslation('project', { keyPrefix: 'TrackingReports' });

  const isContractor = project.type === 'subproject_contractor';

  const columns = getTrackingReportColumns(t, isContractor);

  const allReports = useMemo(
    () => data?.pages.flatMap((page) => page) ?? [],
    [data],
  );

  const hasSearchQuery = searchQuery.trim().length > 0;

  const filteredData = filterTrackingReports(allReports, searchQuery);

  const { mutate: deleteTrackingReport } = useDeleteTrackingReport();

  return (
    <div>
      {isLoading ? (
        <LoadingIndicator />
      ) : !data || isError ? (
        <span>{t('Errors.fetch')}</span>
      ) : filteredData.length === 0 ? (
        <EmptyTablePlaceholder
          title={hasSearchQuery ? t('no_reports_found') : t('no_reports')}
          description={
            hasSearchQuery
              ? undefined
              : isContractor
                ? t('no_reports_description')
                : undefined
          }
          columns={columns}
          action={
            hasSearchQuery
              ? undefined
              : isContractor
                ? () => setTrackingReportFormOpen(true)
                : undefined
          }
        />
      ) : (
        <>
          <TrackingReportsTable
            isContractor={isContractor}
            reports={filteredData}
            onOpenTrackingReport={(report) => {
              setCurrentReport(report);
              setTrackingReportFormOpen(true);
            }}
            onDeleteTrackingReport={deleteTrackingReport}
            columns={columns}
          />
          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? <LoadingIndicator /> : t('load_more')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
